import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Tooltip } from '@radix-ui/themes';

import Text from '../Text';
import TextInput from '../FormStyles/TextInput.jsx';
import { useSmsPreview } from '../../containers/broadcastMessages/hooks/useSmsPreview';
import { MMS_CHAR_LIMIT } from '../../containers/broadcastMessages/utils/constants';
import {
  stripUrlProtocolForCounting,
  normalizeSmsText,
} from '../../containers/broadcastMessages/utils/textHelpers';

// Matches the sample link used by broadcastMessages/hooks/useSmsPreview.js.
const SAMPLE_LINK = 'https://app.airbo.com/digest/sample-00000';
// Matches SmsOptOutFooter::FOOTER / useSmsPreview's own constant.
const OPT_OUT_FOOTER = 'Reply STOP to opt-out.';

// The sent body is "<text>\n\n<link>", so counting includes the link (and the
// opt-out footer when the org has it on), exactly like the Broadcast SMS
// composer counts.
const withSampleLink = text => `${text || ''}\n\n{{link_to_airbo}}`;

const computeCount = (text, smsOptOutFooter) => {
  const expanded = normalizeSmsText(
    text.replaceAll('{{link_to_airbo}}', SAMPLE_LINK),
  );
  const withFooter = smsOptOutFooter
    ? `${expanded}\n${OPT_OUT_FOOTER}`
    : expanded;
  return stripUrlProtocolForCounting(withFooter).length;
};

// A textarea for an SMS body whose counter, limit and normalization notice
// mirror the Broadcast SMS composer. `onChange` receives the new string.
export const SmsTextField = ({
  name,
  value,
  onChange,
  label,
  placeholder,
  disabled = false,
  disabledVariant = 'base',
  labelVariant = 'body2',
  color = 'black.80',
}) => {
  const { t } = useTranslation('shared');
  const smsOptOutFooter = useSelector(
    state => state.currentBoard.smsOptOutFooter,
  );
  const { messageContent, wasNormalized } = useSmsPreview(
    withSampleLink(value),
  );

  const handleChange = e => {
    const newValue = e.target.value;
    const currentValue = value || '';
    const isDeleting = newValue.length < currentValue.length;
    const newCount = computeCount(withSampleLink(newValue), smsOptOutFooter);
    // The counter strips URL protocols, but the model validates the raw
    // field length, so the raw text must fit the limit too.
    const withinLimit =
      newCount <= MMS_CHAR_LIMIT && newValue.length <= MMS_CHAR_LIMIT;

    if (withinLimit || isDeleting) {
      onChange(newValue);
    }
  };

  return (
    <TextInput
      onChange={handleChange}
      value={value || ''}
      placeholder={placeholder}
      name={name}
      label={label}
      labelVariant={labelVariant}
      color={color}
      as="textarea"
      disabled={disabled}
      disabledVariant={disabledVariant}
      extraElements={
        <EditorFooter data-testid="sms-editor-footer">
          {wasNormalized && (
            <NormalizedNotice>
              <Text variant="small3" color="black.60">
                {t('smsTextField.normalizedNotice')}
              </Text>
              <Tooltip content={t('smsTextField.normalizedNoticeTooltip')}>
                <i className="fa-regular fa-circle-info" />
              </Tooltip>
            </NormalizedNotice>
          )}
          <CharCounter data-testid="sms-char-counter">
            <Text variant="small3">
              {t('smsTextField.charCount', {
                count: messageContent.length,
                limit: MMS_CHAR_LIMIT,
              })}
            </Text>
          </CharCounter>
        </EditorFooter>
      }
    />
  );
};

export default SmsTextField;

const EditorFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 8px;
`;

const CharCounter = styled.div`
  background: ${themeGet('colors.black.10')};
  border-radius: 4px;
  padding: 8px;
  flex: 0 0 auto;
`;

const NormalizedNotice = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-right: auto;
  min-width: 0;
  color: ${themeGet('colors.black.60')};

  i {
    font-size: 12px;
  }
`;
